import { useSetState } from 'ahooks'
import { Checkbox, Radio } from 'antd-mobile'
import { RightOutline } from 'antd-mobile-icons'
import classNames from 'classnames'
import React, { useEffect, useMemo } from 'react'

import { SvgIcon, KeywordHightlight, HErrorBlock } from '@/components'
import { SELECT_MODE } from '@/constants'

import './index.less'
import { isNotEmptyArray } from '@/utils'

interface TreeNodeData {
  id: string | number
  name: string
  item_desc?: string
  childs?: TreeNodeData[]
  children?: TreeNodeData[]
  is_click?: number
  metadata?: {
    is_click: number
    all_count: number
  }
  [key: string]: any
}

interface HTreeNodeProps {
  mode?: 'multiple' | 'single' | 'view'
  data: TreeNodeData[]
  originData?: TreeNodeData[]
  title?: string
  value?: any
  type?: 'org' | 'employee'
  needAuth?: boolean
  keyword?: string
  showItemDesc?: boolean
  disabled?: boolean
  maxLimit?: number
  onChange?: (val: any, list?: any[]) => void
  onItemChange?: (val: any, list?: any[]) => void
  onItemClick?: (data: TreeNodeData) => void
  [key: string]: any
}

// 根据id生成map
const generateMap = (data: any[] = [], fieldNames: any): Record<string | number, any> => {
  if (!isNotEmptyArray(data)) {
    return {}
  }
  return data.reduce((result: Record<string | number, any>, item) => {
    result[item[fieldNames?.value]] = item
    if (item[fieldNames?.children]?.length) {
      return { ...result, ...generateMap(item?.[fieldNames?.children], fieldNames) }
    }
    return result
  }, {})
}

const isStringOrNumber = (value: any) => {
  return ['number', 'string'].includes(typeof value)
}

const HTreeNode: React.FC<HTreeNodeProps> = ({
  mode = 'multiple',
  type = 'employee',
  title = '',
  value,
  needAuth = true,
  keyword = '',
  hasShowMore = false,
  showItemDesc = false,
  data,
  fieldNames = { label: 'name', value: 'id', code: '', children: 'childs' },
  originData,
  onChange,
  onItemChange,
  onShowMoreChange,
  onItemClick,
  maxLimit = null,
  ...props
}) => {
  const [{ showMore }, setState] = useSetState<any>({
    showMore: false
  })

  // 是否是查看
  const isView = mode === SELECT_MODE.VIEW

  // 是否是树
  const isTree = fieldNames?.children in (data?.[0] || {})
  const idMap = useMemo(
    () => ({
      ...generateMap(originData || [], fieldNames),
      ...generateMap(data, fieldNames)
    }),
    [data, originData]
  )

  const isOrgTree = type === 'org'
  const isEmployee = type === 'employee'
  const isSingle = mode === SELECT_MODE.SINGLE
  const SelectComponent = mode === SELECT_MODE.MULTIPLE ? Checkbox : Radio

  // 处理 value
  const finalValue = useMemo(() => {
    if (isStringOrNumber(value) || isStringOrNumber(value?.[0])) {
      return value
    }
    const { value: valueName } = fieldNames || {}
    if (isSingle) {
      return value?.[0]?.[valueName] || ''
    }
    return value?.map((x: any) => x?.[valueName])
  }, [value, isSingle, fieldNames])

  const handleChange = (val: any) => {
    if (mode === SELECT_MODE.VIEW) return
    let list: any[] = []
    if (isSingle) {
      val && (list = [idMap[val]])
    } else {
      list = val?.map((x: any) => idMap[x]) || []
    }
    onChange?.(val, list)
  }

  // 单个节点选择
  const handleItemChange = (flag: boolean, item: any) => {
    if (mode === SELECT_MODE.VIEW) {
      onItemClick?.(item)
      return
    }
    onItemChange?.(flag, item)
  }

  const handleShowMore = () => {
    if (typeof onShowMoreChange === 'function') {
      onShowMoreChange?.(!showMore)
    } else {
      setState({ showMore: !showMore })
    }
  }

  const hasMore = useMemo(() => {
    return maxLimit && data?.length > maxLimit && isView && hasShowMore && !keyword
  }, [data, maxLimit, hasShowMore])

  const list = useMemo(() => {
    return maxLimit ? (hasMore && !showMore ? data?.slice(0, maxLimit) : data) : data
  }, [data, maxLimit, hasMore, showMore])

  useEffect(() => {
    setState({ showMore: false })
  }, [data])

  // 如果数据未加载，则不显示
  if ('loaded' in props && !props?.loaded) {
    return null
  }

  if (!list?.length && keyword) {
    return <HErrorBlock size='large' />
  }

  return (
    <div
      className={classNames('h-tree-section', props?.className || '', {
        'h-tree-section-empty': !list?.length
      })}
    >
      {title && <div className='h-tree-section-title'>{title}</div>}
      {list?.length ? (
        <SelectComponent.Group onChange={handleChange} value={finalValue}>
          {list?.map((item, index: number) => {
            const hasChildren = (item?.[fieldNames?.children] || item?.childs)?.length > 0
            // name / value
            const nameText = `${item?.[fieldNames?.label] || item?.name}${item?.[fieldNames?.code] ? `(${item?.[fieldNames?.code]})` : ''}`
            const currentValue = item?.[fieldNames?.value] || item?.id
            // 是否有下级,人员选择器一直展示下级
            const hasNext = isOrgTree ? hasChildren : isTree
            const hasDesc = showItemDesc && item?.item_desc
            const isSelected = isEmployee && value?.includes(currentValue)

            // 是否可点击 0 不可点击 1 可点击
            const isClick = item?.metadata ? item?.metadata?.is_click : item?.is_click

            return (
              <div
                className={classNames('h-tree-item', {
                  'h-tree-item-view': isView,
                  'h-tree-item-with-desc': hasDesc
                })}
                key={`${currentValue}-${nameText}-${index}`}
              >
                <SelectComponent
                  value={currentValue}
                  onChange={(val) => handleItemChange(val, item)}
                  disabled={needAuth && isClick === 0}
                >
                  {isView && (
                    <div className='h-tree-item-icon'>
                      {isTree ? <SvgIcon name='org-tree' /> : nameText?.slice?.(0, 2)}
                    </div>
                  )}
                  <div className='h-tree-item-content'>
                    <div className='h-tree-item-name-wrap'>
                      <span className='h-tree-item-name'>
                        <KeywordHightlight text={nameText} keyword={keyword} />
                      </span>
                      {isTree && !isOrgTree && (
                        <span className='h-tree-item-count'>
                          （{item.metadata?.all_count || 0}人）
                        </span>
                      )}
                    </div>
                    {hasDesc && (
                      <div className='h-tree-item-desc'>
                        <KeywordHightlight text={item?.item_desc || ''} keyword={keyword} />
                      </div>
                    )}
                  </div>
                </SelectComponent>
                {hasNext && (
                  <div
                    className={classNames('h-tree-item-next', {
                      'h-tree-item-next-disabled': isSelected
                    })}
                    onClick={() => !isSelected && onItemClick?.(item)}
                  >
                    下级
                  </div>
                )}
                {isView && !isTree && (
                  <div className='h-tree-item-arrow-icon' onClick={() => onItemClick?.(item)}>
                    <RightOutline />
                  </div>
                )}
              </div>
            )
          })}
          {hasMore && (
            <div className='h-tree-item-more' onClick={() => handleShowMore()}>
              {showMore ? '收起' : '查看更多'}
            </div>
          )}
        </SelectComponent.Group>
      ) : (
        <HErrorBlock size='large' />
      )}
    </div>
  )
}

export default HTreeNode
